import React, { useState } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    Grid,
    CircularProgress,
    Alert,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
} from "@mui/material";
import { useAuth } from "@clerk/clerk-react";
import { callBackend, formatBackendError, formatNetworkError } from "../api";

const Analytics = () => {
    const { getToken } = useAuth();
    const [startDate, setStartDate] = useState("");
    const [endDate, setEndDate] = useState("");
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchAnalytics = async () => {
        setLoading(true);
        setError(null);
        try {
            const token = await getToken();
            const params = new URLSearchParams();
            if (startDate) params.append('startDate', startDate);
            if (endDate) params.append('endDate', endDate);
            
            const result = await callBackend(`/analytics/translations?${params}`, { token });

            // `{ error: "..." }` comes back with HTTP 200, so both cases land here.
            if (!result.ok) {
                setError(formatBackendError(result, "Failed to fetch analytics"));
                setStats(null);
                return;
            }

            setStats(result.data);
        } catch (err) {
            setError(formatNetworkError(err, "Failed to fetch analytics"));
            setStats(null);
        } finally {
            setLoading(false);
        }
    };

    const languageNames = {
        ES: "Spanish",
        PT: "Portuguese", 
        RU: "Russian",
        AR: "Arabic",
        TR: "Turkish",
        RO: "Romanian",
        ZH: "Chinese",
        JA: "Japanese",
        DE: "German",
        KO: "Korean",
        PL: "Polish",
        FR: "French"
    };

    // Defensive: an unexpected payload must not white-page the whole page.
    const languageBreakdown = (stats && stats.languageBreakdown) || {};

    return (
        <Box sx={{ maxWidth: 1200, margin: "auto", padding: 2 }}>
            <Typography variant="h4" gutterBottom>
                Translation Analytics
            </Typography>

            {/* Date Selection */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Select Date Range
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                        <TextField
                            type="date"
                            label="Start Date"
                            value={startDate}
                            onChange={(e) => setStartDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                        />
                        <TextField
                            type="date"
                            label="End Date (Optional)"
                            value={endDate}
                            onChange={(e) => setEndDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            size="small"
                            helperText="Leave empty for 'up until now'"
                        />
                        <Button 
                            variant="contained" 
                            onClick={fetchAnalytics}
                            disabled={loading}
                            sx={{ height: 40 }}
                        >
                            {loading ? <CircularProgress size={20} color="inherit" /> : "Get Analytics"}
                        </Button>
                    </Box>
                    {stats && stats.dateRange && (
                        <Typography variant="body2" color="text.secondary">
                            Date Range: {stats.dateRange.startDate} to {stats.dateRange.endDate}
                        </Typography>
                    )}
                </CardContent>
            </Card>

            {/* Error Display */}
            {error && (
                <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                </Alert>
            )}

            {/* Statistics Display */}
            {stats && (
                <>
                    {/* Main Stats */}
                    <Grid container spacing={3} sx={{ mb: 3 }}>
                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <Typography variant="h6" color="primary.main" gutterBottom>
                                        Individual Videos Completed
                                    </Typography>
                                    <Typography variant="h2" color="primary.main" sx={{ fontWeight: 'bold' }}>
                                        {stats.videosCompleted}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Total translated videos
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Card>
                                <CardContent sx={{ textAlign: 'center' }}>
                                    <Typography variant="h6" color="success.main" gutterBottom>
                                        Complete Lessons
                                    </Typography>
                                    <Typography variant="h2" color="success.main" sx={{ fontWeight: 'bold' }}>
                                        {stats.lessonsCompleted}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        Fully translated lessons
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>

                    {/* Language Breakdown */}
                    {Object.keys(languageBreakdown).length > 0 && (
                        <Card>
                            <CardContent>
                                <Typography variant="h6" gutterBottom>
                                    Videos by Language
                                </Typography>
                                <TableContainer component={Paper} sx={{ mt: 2 }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell><strong>Language</strong></TableCell>
                                                <TableCell align="right"><strong>Videos Completed</strong></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {Object.entries(languageBreakdown)
                                                .sort(([,a], [,b]) => b - a)
                                                .map(([langCode, count]) => (
                                                <TableRow key={langCode}>
                                                    <TableCell>
                                                        {languageNames[langCode] || langCode}
                                                        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                                            ({langCode})
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                                            {count}
                                                        </Typography>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </CardContent>
                        </Card>
                    )}

                    {/* Summary */}
                    <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                        <Typography variant="body2" color="text.secondary">
                            <strong>Summary:</strong> In the selected period, {stats.videosCompleted} individual video translations
                            were completed across {Object.keys(languageBreakdown).length} languages,
                            resulting in {stats.lessonsCompleted} fully translated lessons.
                        </Typography>
                    </Box>
                </>
            )}

            {/* Initial State */}
            {!stats && !loading && (
                <Card>
                    <CardContent sx={{ textAlign: 'center', py: 6 }}>
                        <Typography variant="h6" color="text.secondary" gutterBottom>
                            Select a date range and click "Get Analytics" to view translation statistics
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Leave end date empty to get data from start date until now
                        </Typography>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

export default Analytics;